import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { db } from "../config/firebase";
import { useAuth } from "../contexts/AuthContext";

// ─────────────────────────────────────────────────────────────────────────────
// KA brand
// ─────────────────────────────────────────────────────────────────────────────
const KA_NAVY  = "#233C5F";
const KA_RED   = "#E83530";
const KA_GOLD  = "#A29164";
const KA_GREY  = "#f1f2f5";

// ─────────────────────────────────────────────────────────────────────────────
// Utility: today's date in Amman
// ─────────────────────────────────────────────────────────────────────────────
function todayInAmman() {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Amman",
    year: "numeric", month: "2-digit", day: "2-digit",
  }).format(new Date());
}

function daysAgoInAmman(n) {
  const d = new Date(Date.now() - n * 24 * 60 * 60 * 1000);
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Amman",
    year: "numeric", month: "2-digit", day: "2-digit",
  }).format(d);
}

// Return YYYY-MM-DD strings for the last N days, oldest first
function lastNDays(n) {
  const out = [];
  for (let i = n - 1; i >= 0; i--) out.push(daysAgoInAmman(i));
  return out;
}

// Pretty date for display: "Mon, Aug 18"
function prettyDate(dateKey) {
  return new Date(dateKey + "T00:00:00").toLocaleDateString("en-US", {
    weekday: "short", month: "short", day: "numeric",
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// CSV export helper
// ─────────────────────────────────────────────────────────────────────────────
function toCsv(rows, columns) {
  const escape = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v).replace(/"/g, '""');
    return /[",\n]/.test(s) ? `"${s}"` : s;
  };
  const header = columns.map((c) => c.label).join(",");
  const body = rows
    .map((r) => columns.map((c) => escape(c.get(r))).join(","))
    .join("\n");
  return header + "\n" + body;
}

function downloadCsv(filename, csvString) {
  const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────
export default function AttendanceEmailAnalytics() {
  const { user } = useAuth();

  // Access gate: superadmin OR user has the "attendance-emails" tool
  // (Firestore rules also enforce this; this is UI-side friendliness.)
  const hasAccess = !!user;  // Assumes route-level guard already filtered. Refine if needed.

  // View mode: "single" or "range"
  const [viewMode, setViewMode] = useState("single");

  // Single-day pick
  const [singleDate, setSingleDate] = useState(daysAgoInAmman(1)); // yesterday by default

  // Range pick
  const [rangeStart, setRangeStart] = useState(daysAgoInAmman(6));
  const [rangeEnd,   setRangeEnd]   = useState(daysAgoInAmman(0));

  // Rolling 14-day chart data (loaded once + when refreshed)
  const [chartData, setChartData]   = useState([]);
  const [chartLoading, setChartLoading] = useState(true);

  // Currently viewed run data
  const [viewData, setViewData]     = useState(null); // { runs: [...], recipients: [...] }
  const [viewLoading, setViewLoading] = useState(false);

  // Recipient list filters
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // "all" | "sent" | "failed"

  // ── Load the rolling 14-day chart ──────────────────────────────────────
  useEffect(() => {
    let alive = true;
    (async () => {
      setChartLoading(true);
      try {
        const dates = lastNDays(14);
        const results = await Promise.all(
          dates.map(async (dateKey) => {
            const snap = await getDocs(
              query(collection(db, "emailRuns"), where("date", "==", dateKey))
            );
            let total = 0, sent = 0, failed = 0;
            snap.forEach((doc) => {
              const d = doc.data();
              // If multiple runs on the same day, take the most complete (highest emailsSent)
              if ((d.emailsSent ?? 0) > sent) {
                sent = d.emailsSent ?? 0;
                total = d.absencesQualifying ?? 0;
                failed = (d.recipientCount ?? 0) - (d.emailsSent ?? 0);
              }
            });
            return { date: dateKey, total, sent, failed };
          })
        );
        if (alive) setChartData(results);
      } catch (err) {
        console.error("Failed to load chart data:", err);
      } finally {
        if (alive) setChartLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // ── Load view data when date selection changes ─────────────────────────
  useEffect(() => {
    let alive = true;
    (async () => {
      setViewLoading(true);
      try {
        // Determine which dates to fetch
        let dates;
        if (viewMode === "single") {
          dates = [singleDate];
        } else {
          // Range: build inclusive list
          dates = [];
          const start = new Date(rangeStart + "T00:00:00");
          const end   = new Date(rangeEnd   + "T00:00:00");
          const cur = new Date(start);
          while (cur <= end) {
            dates.push(new Intl.DateTimeFormat("en-CA", {
              timeZone: "UTC",
              year: "numeric", month: "2-digit", day: "2-digit",
            }).format(cur));
            cur.setDate(cur.getDate() + 1);
          }
          if (dates.length > 30) dates = dates.slice(-30);  // cap for perf
        }

        // Fetch each run doc + collect recipients
        const runs = [];
        const recipients = [];
        for (const dateKey of dates) {
          const snap = await getDocs(
            query(collection(db, "emailRuns"), where("date", "==", dateKey))
          );
          snap.forEach((doc) => {
            const d = doc.data();
            runs.push({ id: doc.id, ...d });
            if (Array.isArray(d.results)) {
              d.results.forEach((r) => {
                recipients.push({
                  ...r,
                  runDate: d.date,
                  triggeredBy: d.triggeredBy,
                  testMode: d.testMode,
                });
              });
            }
          });
        }

        if (alive) setViewData({ runs, recipients });
      } catch (err) {
        console.error("Failed to load run data:", err);
        if (alive) setViewData({ runs: [], recipients: [], error: err.message });
      } finally {
        if (alive) setViewLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [viewMode, singleDate, rangeStart, rangeEnd]);

  // ── Filtered recipient list ────────────────────────────────────────────
  const filteredRecipients = useMemo(() => {
    if (!viewData) return [];
    let list = viewData.recipients;
    if (statusFilter === "sent")   list = list.filter((r) => r.sent === true);
    if (statusFilter === "failed") list = list.filter((r) => r.sent === false);
    const q = searchText.trim().toLowerCase();
    if (q) {
      list = list.filter((r) =>
        (r.studentName || "").toLowerCase().includes(q) ||
        (r.to          || "").toLowerCase().includes(q) ||
        (r.course      || "").toLowerCase().includes(q) ||
        (r.teacherEmail|| "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [viewData, statusFilter, searchText]);

  // ── Aggregate stats across all runs currently in view ──────────────────
  const stats = useMemo(() => {
    if (!viewData || !viewData.runs.length) {
      return { runsCount: 0, totalRecords: 0, absencesQualifying: 0, emailsSent: 0, failed: 0 };
    }
    // If multiple runs for the same date, take the highest emailsSent per date
    const byDate = {};
    viewData.runs.forEach((r) => {
      if (!byDate[r.date] || (r.emailsSent ?? 0) > (byDate[r.date].emailsSent ?? 0)) {
        byDate[r.date] = r;
      }
    });
    const bestRuns = Object.values(byDate);
    return {
      runsCount: bestRuns.length,
      totalRecords: bestRuns.reduce((s, r) => s + (r.totalRecords ?? 0), 0),
      absencesQualifying: bestRuns.reduce((s, r) => s + (r.absencesQualifying ?? 0), 0),
      emailsSent: bestRuns.reduce((s, r) => s + (r.emailsSent ?? 0), 0),
      failed: bestRuns.reduce((s, r) => s + ((r.recipientCount ?? 0) - (r.emailsSent ?? 0)), 0),
    };
  }, [viewData]);

  // ── CSV export ─────────────────────────────────────────────────────────
  const handleExport = () => {
    if (!filteredRecipients.length) return;
    const columns = [
      { label: "Date",          get: (r) => r.runDate },
      { label: "Student Name",  get: (r) => r.studentName },
      { label: "Student Email", get: (r) => r.to },
      { label: "Course",        get: (r) => r.course },
      { label: "Period",        get: (r) => r.period },
      { label: "Teacher Email", get: (r) => r.teacherEmail },
      { label: "Status",        get: (r) => r.sent ? "sent" : "failed" },
      { label: "Reason",        get: (r) => r.sent ? "" : (r.reason || "") },
      { label: "Test Mode",     get: (r) => r.testMode ? "yes" : "no" },
      { label: "Triggered By",  get: (r) => r.triggeredBy },
    ];
    const csv = toCsv(filteredRecipients, columns);
    const label = viewMode === "single" ? singleDate : `${rangeStart}_to_${rangeEnd}`;
    downloadCsv(`attendance-emails-${label}.csv`, csv);
  };

  // ─────────────────────────────────────────────────────────────────────
  if (!hasAccess) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: "#888" }}>
        You do not have access to this page.
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto", padding: "24px" }}>
      <h1 style={{ color: KA_NAVY, marginBottom: 4 }}>Attendance Email Analytics</h1>
      <p style={{ color: "#666", marginTop: 0, fontSize: 14 }}>
        Records of automated and manual absence notification runs.
      </p>

      {/* ── Rolling 14-day chart ───────────────────────────────────── */}
      <RollingChart data={chartData} loading={chartLoading} />

      {/* ── View mode toggle + date pickers ────────────────────────── */}
      <div style={{
        display: "flex", gap: 16, alignItems: "flex-end", flexWrap: "wrap",
        padding: 16, background: KA_GREY, borderRadius: 8, marginTop: 24, marginBottom: 16,
      }}>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={() => setViewMode("single")}
            style={tabButtonStyle(viewMode === "single")}
          >Single Day</button>
          <button
            onClick={() => setViewMode("range")}
            style={tabButtonStyle(viewMode === "range")}
          >Date Range</button>
        </div>

        {viewMode === "single" ? (
          <label style={fieldStyle}>
            <span style={labelStyle}>Date (Amman)</span>
            <input type="date" value={singleDate} onChange={(e) => setSingleDate(e.target.value)}
              style={inputStyle} />
          </label>
        ) : (
          <>
            <label style={fieldStyle}>
              <span style={labelStyle}>Start Date</span>
              <input type="date" value={rangeStart} onChange={(e) => setRangeStart(e.target.value)}
                style={inputStyle} />
            </label>
            <label style={fieldStyle}>
              <span style={labelStyle}>End Date</span>
              <input type="date" value={rangeEnd} onChange={(e) => setRangeEnd(e.target.value)}
                style={inputStyle} />
            </label>
          </>
        )}
      </div>

      {/* ── Summary cards ──────────────────────────────────────────── */}
      {viewLoading ? (
        <div style={{ padding: 32, textAlign: "center", color: "#888" }}>Loading...</div>
      ) : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 20 }}>
            <StatCard label="Runs"                value={stats.runsCount} />
            <StatCard label="Total Records"       value={stats.totalRecords.toLocaleString()} />
            <StatCard label="Unexcused Absences"  value={stats.absencesQualifying.toLocaleString()} accent={KA_GOLD} />
            <StatCard label="Emails Sent"         value={stats.emailsSent.toLocaleString()} accent={KA_NAVY} />
            <StatCard label="Failed"              value={stats.failed.toLocaleString()} accent={stats.failed > 0 ? KA_RED : "#999"} />
          </div>

          {/* Per-run detail cards */}
          {viewData?.runs?.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <h3 style={{ color: KA_NAVY, fontSize: 16, marginBottom: 8 }}>Run Details</h3>
              {viewData.runs.map((run) => (
                <RunCard key={run.id + run.sentAt} run={run} />
              ))}
            </div>
          )}

          {/* Recipient table */}
          <div style={{ marginTop: 24 }}>
            <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginBottom: 8 }}>
              <h3 style={{ color: KA_NAVY, fontSize: 16, margin: 0 }}>
                Recipients ({filteredRecipients.length})
              </h3>
              <input
                type="text"
                placeholder="Search name, email, course, teacher..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ ...inputStyle, flex: 1, minWidth: 240 }}
              />
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={inputStyle}>
                <option value="all">All</option>
                <option value="sent">Sent only</option>
                <option value="failed">Failed only</option>
              </select>
              <button
                onClick={handleExport}
                disabled={!filteredRecipients.length}
                style={{
                  padding: "8px 14px", background: KA_NAVY, color: "#fff",
                  border: "none", borderRadius: 4, fontWeight: 600,
                  cursor: filteredRecipients.length ? "pointer" : "not-allowed",
                  opacity: filteredRecipients.length ? 1 : 0.5,
                }}
              >
                ↓ Export CSV
              </button>
            </div>

            <RecipientTable rows={filteredRecipients} />
          </div>
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Rolling chart — 14 days of send counts
// ─────────────────────────────────────────────────────────────────────────────
function RollingChart({ data, loading }) {
  if (loading) return <div style={{ padding: 20, color: "#888" }}>Loading trend chart...</div>;
  if (!data.length) return null;

  const maxSent = Math.max(1, ...data.map((d) => d.sent));

  return (
    <div style={{ padding: 16, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, marginTop: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 12 }}>
        <h3 style={{ color: KA_NAVY, fontSize: 15, margin: 0 }}>Last 14 days</h3>
        <div style={{ fontSize: 12, color: "#888" }}>
          Emails sent per day
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 4, height: 120 }}>
        {data.map((d) => {
          const heightPct = (d.sent / maxSent) * 100;
          return (
            <div key={d.date} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center" }}>
              <div style={{
                width: "100%",
                height: `${Math.max(heightPct, 2)}%`,
                background: d.sent > 0 ? KA_NAVY : "#e5e7eb",
                borderRadius: "3px 3px 0 0",
                transition: "height 0.3s ease",
              }}
                title={`${prettyDate(d.date)}: ${d.sent} sent`}
              />
              <div style={{ fontSize: 10, color: "#888", marginTop: 4, whiteSpace: "nowrap" }}>
                {d.date.slice(-2)}
              </div>
              <div style={{ fontSize: 11, color: KA_NAVY, fontWeight: 600 }}>
                {d.sent || ""}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Small display components
// ─────────────────────────────────────────────────────────────────────────────
function StatCard({ label, value, accent }) {
  return (
    <div style={{
      padding: "14px 16px", background: "#fff",
      border: "1px solid #e5e7eb", borderRadius: 8,
    }}>
      <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase", fontWeight: 700, letterSpacing: 0.5 }}>
        {label}
      </div>
      <div style={{ fontSize: 24, fontWeight: 700, color: accent || KA_NAVY, marginTop: 4 }}>
        {value}
      </div>
    </div>
  );
}

function RunCard({ run }) {
  const sentPct = run.recipientCount ? Math.round((run.emailsSent / run.recipientCount) * 100) : 0;
  return (
    <div style={{
      padding: 12, background: "#fff",
      border: "1px solid #e5e7eb", borderRadius: 6, marginBottom: 8,
      display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12,
    }}>
      <div>
        <div style={{ fontWeight: 700, color: KA_NAVY }}>{prettyDate(run.date)}</div>
        <div style={{ fontSize: 12, color: "#666" }}>
          {run.sentAt && new Date(run.sentAt).toLocaleString()} &middot;{" "}
          Triggered by: <code style={{ fontSize: 11 }}>{run.triggeredBy}</code>
          {run.testMode && <span style={{ color: KA_GOLD, marginLeft: 6 }}>(test mode)</span>}
          {run.dryRun && <span style={{ color: KA_GOLD, marginLeft: 6 }}>(dry run)</span>}
        </div>
      </div>
      <div style={{ textAlign: "right", fontSize: 13 }}>
        <div><strong>{run.emailsSent}</strong> / {run.recipientCount} emails sent ({sentPct}%)</div>
        <div style={{ color: "#666", fontSize: 12 }}>
          from {run.absencesQualifying} unexcused abs of {run.totalRecords} total records
        </div>
      </div>
    </div>
  );
}

function RecipientTable({ rows }) {
  if (!rows.length) {
    return <div style={{ padding: 20, textAlign: "center", color: "#888", background: "#fff", border: "1px solid #e5e7eb", borderRadius: 6 }}>
      No recipients matching filters.
    </div>;
  }
  return (
    <div style={{ overflowX: "auto", background: "#fff", border: "1px solid #e5e7eb", borderRadius: 6 }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr style={{ background: KA_GREY, textAlign: "left" }}>
            <th style={thStyle}>Date</th>
            <th style={thStyle}>Student</th>
            <th style={thStyle}>Email</th>
            <th style={thStyle}>Course</th>
            <th style={thStyle}>Period</th>
            <th style={thStyle}>Teacher</th>
            <th style={thStyle}>Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} style={{ borderTop: "1px solid #f1f2f5" }}>
              <td style={tdStyle}>{r.runDate}</td>
              <td style={tdStyle}>{r.studentName || "—"}</td>
              <td style={tdStyle}><code style={{ fontSize: 11 }}>{r.to || "—"}</code></td>
              <td style={tdStyle}>{r.course || "—"}</td>
              <td style={tdStyle}>{r.period || "—"}</td>
              <td style={tdStyle}><code style={{ fontSize: 11 }}>{r.teacherEmail || "—"}</code></td>
              <td style={tdStyle}>
                {r.sent
                  ? <span style={{ color: "#2E7D32", fontWeight: 600 }}>✓ Sent</span>
                  : <span style={{ color: KA_RED, fontWeight: 600 }} title={r.reason}>✗ Failed</span>
                }
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Styles
// ─────────────────────────────────────────────────────────────────────────────
const fieldStyle = { display: "flex", flexDirection: "column", fontSize: 13 };
const labelStyle = { fontWeight: 600, marginBottom: 4, color: KA_NAVY };
const inputStyle = { padding: "6px 10px", border: "1px solid #d1d5db", borderRadius: 4, fontSize: 13 };
const thStyle    = { padding: "8px 12px", fontWeight: 700, color: KA_NAVY, fontSize: 12 };
const tdStyle    = { padding: "8px 12px", color: "#333" };
const tabButtonStyle = (active) => ({
  padding: "8px 14px",
  background: active ? KA_NAVY : "#fff",
  color: active ? "#fff" : KA_NAVY,
  border: `1px solid ${KA_NAVY}`,
  borderRadius: 4,
  cursor: "pointer",
  fontWeight: 600,
  fontSize: 13,
});